// src/components/ItemDetailsModal.js - Fixed version
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  TextField,
} from "@mui/material";
import {
  Close,
  Star,
  LocalShipping,
  VerifiedUser,
  ArrowBack,
  ArrowForward,
  Add,
  Remove,
  ShoppingCart,
  Info,
  Description,
} from "@mui/icons-material";

const ItemDetailsModal = ({
  open,
  onClose,
  item,
  formatPrice,
  addItemToCart,
  canAddMoreToCart,
  setSelectedItem,
  setShowItemCheckout,
  setShowCart, // Add this prop
  getItemCartQuantity,
  cartItems = [], // Add cartItems prop for real-time accuracy
  primaryBlue,
  glassStyles,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");

  // Set default selections when modal opens
  useEffect(() => {
    if (item) {
      // Reset quantity to minimum or 1
      setQuantity(item.minOrderQuantity || 1);

      // Set default size if available
      if (item.sizes && item.sizes.length > 0) {
        setSelectedSize(item.sizes[0]);
      } else {
        setSelectedSize("");
      }

      // Set default color if available
      if (item.colors && item.colors.length > 0) {
        setSelectedColor(item.colors[0]);
      } else {
        setSelectedColor("");
      }

      // Reset selected image
      setSelectedImage(0);
    }
  }, [item]);

  // Combine main image with additional images if they exist
  const allImages = [item?.imageUrl, ...(item?.additionalImages || [])].filter(
    Boolean
  );

  // Combine images and videos for the media gallery
  const allMedia = [
    ...allImages.map((url) => ({ type: "image", url })),
    ...(item?.videoUrl ? [{ type: "video", url: item.videoUrl }] : []),
  ];

  // Helper function to determine if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    // Check for common video patterns
    return (
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("vimeo.com") ||
      url.match(/\.(mp4|mov|avi|webm|mkv)(\?|$)/i)
    );
  };

  // Get YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Get Vimeo video ID from URL
  const getVimeoVideoId = (url) => {
    const regExp = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddToCart = () => {
    // Create a modified item with selected options
    const itemWithOptions = {
      ...item,
      selectedSize,
      selectedColor,
      selectedOptions: {
        size: selectedSize,
        color: selectedColor,
      },
    };

    addItemToCart(itemWithOptions, quantity);
    onClose(); // Close the modal after adding to cart
  };

  const handleBuyNow = () => {
    // Create a modified item with selected options
    const itemWithOptions = {
      ...item,
      selectedSize,
      selectedColor,
      selectedOptions: {
        size: selectedSize,
        color: selectedColor,
      },
    };

    // Add to cart first
    addItemToCart(itemWithOptions, quantity);

    // Close modal
    onClose();

    // Navigate to CartPage instead of ItemCheckoutPage
    setTimeout(() => {
      if (setShowCart) {
        setShowCart(true);
      }
    }, 100);
  };

  const handleIncreaseQuantity = () => {
    // Use cartItems prop for real-time accuracy
    const existingCartItem = cartItems.find((ci) => ci.id === item.id);
    const cartQty = existingCartItem ? existingCartItem.quantity : 0;
    // Only use limits if they are positive numbers
    const stockLimitVal =
      item.stockQuantity && item.stockQuantity > 0
        ? item.stockQuantity
        : Infinity;
    const maxOrderLimitVal =
      item.maxOrderQuantity && item.maxOrderQuantity > 0
        ? item.maxOrderQuantity
        : Infinity;
    const maxAllowed = Math.min(stockLimitVal, maxOrderLimitVal);

    // If no limit, allow increasing
    if (maxAllowed === Infinity) {
      setQuantity(quantity + 1);
    } else {
      const remainingAllowed = Math.max(0, maxAllowed - cartQty);
      if (quantity < remainingAllowed) {
        setQuantity(quantity + 1);
      }
    }
  };

  const handleDecreaseQuantity = () => {
    if (quantity > item.minOrderQuantity) {
      setQuantity(quantity - 1);
    }
  };

  const handleChangeQuantity = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      // Use cartItems prop for real-time accuracy
      const existingCartItem = cartItems.find((ci) => ci.id === item.id);
      const cartQty = existingCartItem ? existingCartItem.quantity : 0;
      // Only use limits if they are positive numbers
      const stockLimitVal =
        item.stockQuantity && item.stockQuantity > 0
          ? item.stockQuantity
          : Infinity;
      const maxOrderLimitVal =
        item.maxOrderQuantity && item.maxOrderQuantity > 0
          ? item.maxOrderQuantity
          : Infinity;
      const maxAllowed = Math.min(stockLimitVal, maxOrderLimitVal);

      let newQuantity = Math.max(value, item.minOrderQuantity || 1);

      // Only cap the quantity if there's an actual limit
      if (maxAllowed !== Infinity) {
        const remainingAllowed = Math.max(1, maxAllowed - cartQty);
        newQuantity = Math.min(newQuantity, remainingAllowed);
      }

      setQuantity(newQuantity > 0 ? newQuantity : 1);
    }
  };

  const handleNextImage = () => {
    setSelectedImage((prev) => (prev + 1) % allMedia.length);
  };

  const handlePrevImage = () => {
    setSelectedImage((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  };

  if (!item) return null;

  // Use cartItems prop directly for real-time accuracy
  const cartItem = cartItems.find((ci) => ci.id === item.id);
  const cartQuantity = cartItem ? cartItem.quantity : 0;
  // Only use limits if they are positive numbers
  const stockLimit =
    item.stockQuantity && item.stockQuantity > 0
      ? item.stockQuantity
      : Infinity;
  const maxOrderLimit =
    item.maxOrderQuantity && item.maxOrderQuantity > 0
      ? item.maxOrderQuantity
      : Infinity;
  const maxAllowedQuantity = Math.min(stockLimit, maxOrderLimit);
  // Max is reached only if there's a limit AND cart already has that many (can't add more)
  const isMaxQuantityReached =
    maxAllowedQuantity !== Infinity && cartQuantity >= maxAllowedQuantity;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          ...glassStyles,
          bgcolor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(30px)",
          borderRadius: "20px",
          overflowY: "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", color: primaryBlue[800] }}
        >
          {item.name}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            ...glassStyles,
            bgcolor: "rgba(255, 255, 255, 0.3)",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.5)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Left side - Media (Images & Videos) */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: "relative" }}>
              <Card
                elevation={0}
                sx={{
                  ...glassStyles,
                  bgcolor: "rgba(255, 255, 255, 0.7)",
                  height: 400,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {allMedia[selectedImage]?.type === "video" ? (
                  // Render video player
                  <Box sx={{ width: "100%", height: "100%", p: 1 }}>
                    {(() => {
                      const videoUrl = allMedia[selectedImage].url;
                      const youtubeId = getYouTubeVideoId(videoUrl);
                      const vimeoId = getVimeoVideoId(videoUrl);

                      if (youtubeId) {
                        return (
                          <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${youtubeId}`}
                            title="Product Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: "12px" }}
                          />
                        );
                      } else if (vimeoId) {
                        return (
                          <iframe
                            width="100%"
                            height="100%"
                            src={`https://player.vimeo.com/video/${vimeoId}`}
                            title="Product Video"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            style={{ borderRadius: "12px" }}
                          />
                        );
                      } else {
                        return (
                          <video
                            width="100%"
                            height="100%"
                            controls
                            style={{
                              borderRadius: "12px",
                              objectFit: "contain",
                            }}
                          >
                            <source src={videoUrl} type="video/mp4" />
                            Your browser does not support the video tag.
                          </video>
                        );
                      }
                    })()}
                  </Box>
                ) : (
                  // Render image
                  <CardMedia
                    component="img"
                    image={allMedia[selectedImage]?.url || allImages[0]}
                    alt={item.name}
                    sx={{
                      maxHeight: "100%",
                      objectFit: "contain",
                      p: 2,
                    }}
                  />
                )}
              </Card>

              {allMedia.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                    }}
                  >
                    <ArrowForward />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Thumbnail media */}
            {allMedia.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 2,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {allMedia.map((media, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    sx={{
                      width: 60,
                      height: 60,
                      border:
                        selectedImage === idx
                          ? `2px solid ${primaryBlue[500]}`
                          : `1px solid ${primaryBlue[200]}`,
                      borderRadius: 1,
                      overflow: "hidden",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: `0 4px 10px rgba(0,0,0,0.1)`,
                      },
                    }}
                  >
                    {media.type === "video" ? (
                      <>
                        {/* Video thumbnail - use first frame or default video icon */}
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            bgcolor: primaryBlue[100],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: primaryBlue[600],
                            fontSize: "24px",
                          }}
                        >
                          ▶
                        </Box>
                        {/* Small video indicator */}
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 2,
                            right: 2,
                            width: 12,
                            height: 12,
                            bgcolor: "rgba(0,0,0,0.7)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "8px",
                            color: "white",
                          }}
                        >
                          ▶
                        </Box>
                      </>
                    ) : (
                      <img
                        src={media.url}
                        alt={`Thumbnail ${idx}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Key highlights - Show only the most important ones */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: primaryBlue[700],
                  fontWeight: 600,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Info fontSize="small" /> Key Highlights
              </Typography>

              <List disablePadding>
                {item.warranty && (
                  <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <VerifiedUser
                        fontSize="small"
                        sx={{ color: primaryBlue[500] }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${item.warranty} Warranty`}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: primaryBlue[700],
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Grid>

          {/* Right side - Essential Details */}
          <Grid item xs={12} md={6}>
            <Box>
              {/* Brand and Category - Important for user decisions */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 2,
                }}
              >
                {item.brand && item.brand !== "No Brand" && (
                  <Chip
                    label={item.brand}
                    sx={{
                      bgcolor: primaryBlue[50],
                      color: primaryBlue[700],
                      fontWeight: 600,
                      border: `1px solid ${primaryBlue[200]}`,
                    }}
                  />
                )}

                {item.category && (
                  <Chip
                    label={item.category}
                    sx={{
                      bgcolor: "rgba(0, 114, 206, 0.8)",
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              {/* Rating - Important for purchase decisions */}
              {item.rating > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Rating
                    value={item.rating}
                    readOnly
                    precision={0.5}
                    size="small"
                    sx={{
                      color: "#D4AF37",
                      mr: 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: primaryBlue[700],
                    }}
                  >
                    {item.rating}.0 / 5
                  </Typography>
                </Box>
              )}

              {/* Price section - Critical for purchasing */}
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: "rgba(0, 114, 206, 0.05)",
                  border: `1px solid ${primaryBlue[100]}`,
                }}
              >
                {/* Price Display Section */}
                <Box sx={{ mb: 2 }}>
                  {/* Main Price Row: Selling Price + MRP (strikethrough) + Discount Badge */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1.5,
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        background: `linear-gradient(45deg, ${primaryBlue[700]}, ${primaryBlue[500]})`,
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {formatPrice(item.sellingPrice)}
                    </Typography>

                    {item.compareAtPrice &&
                      item.compareAtPrice > item.sellingPrice && (
                        <>
                          <Typography
                            variant="h6"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                              fontWeight: 500,
                            }}
                          >
                            MRP {formatPrice(item.compareAtPrice)}
                          </Typography>
                          <Chip
                            label={`${Math.round(
                              (1 - item.sellingPrice / item.compareAtPrice) *
                                100
                            )}% OFF`}
                            size="small"
                            color="success"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 1,
                            }}
                          />
                        </>
                      )}
                  </Box>

                  {/* GST Breakdown Section */}
                  {(() => {
                    const basePrice = item.basePrice || item.sellingPrice || 0;
                    // Handle all edge cases: undefined, null, empty string, NaN
                    const rawGst = item.gstPercentage;
                    const gstPercentage =
                      rawGst !== undefined &&
                      rawGst !== null &&
                      rawGst !== "" &&
                      !isNaN(Number(rawGst))
                        ? Number(rawGst)
                        : 18;
                    const gstAmount = (basePrice * gstPercentage) / 100;
                    const totalWithGST = item.sellingPrice + gstAmount;

                    return (
                      <Box
                        sx={{
                          bgcolor: "rgba(0, 114, 206, 0.05)",
                          borderRadius: 2,
                          p: 1.5,
                          mb: 1.5,
                          border: `1px solid ${primaryBlue[100]}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: primaryBlue[700],
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Price Breakdown
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.3,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Selling Price:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatPrice(item.sellingPrice)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.3,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            + GST ({gstPercentage}%):
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="text.secondary"
                          >
                            {formatPrice(gstAmount)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderTop: `1px dashed ${primaryBlue[200]}`,
                            pt: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={primaryBlue[700]}
                          >
                            Total (incl. GST):
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color={primaryBlue[700]}
                          >
                            {formatPrice(totalWithGST)}
                          </Typography>
                        </Box>

                        {/* You Save section - only show if there's a discount */}
                        {/* Savings = MRP - (Selling Price + GST) */}
                        {item.compareAtPrice &&
                          item.compareAtPrice > totalWithGST && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                bgcolor: "rgba(76, 175, 80, 0.1)",
                                borderRadius: 1,
                                p: 0.5,
                                mt: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="success.main"
                              >
                                💰 You Save:
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                color="success.main"
                              >
                                {formatPrice(
                                  item.compareAtPrice - totalWithGST
                                )}
                              </Typography>
                            </Box>
                          )}
                      </Box>
                    );
                  })()}

                  <Typography
                    variant="body2"
                    sx={{
                      color: primaryBlue[600],
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <LocalShipping fontSize="small" />
                    Free shipping on orders above ₹2,999
                  </Typography>
                </Box>
              </Box>

              {/* Sizes - Critical choice for customers */}
              {item.sizes && item.sizes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: primaryBlue[800],
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Select Size{" "}
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {item.sizes.map((size, idx) => (
                      <Chip
                        key={idx}
                        label={size}
                        onClick={() => setSelectedSize(size)}
                        sx={{
                          bgcolor:
                            selectedSize === size
                              ? `${primaryBlue[500]}`
                              : "rgba(0, 114, 206, 0.1)",
                          color:
                            selectedSize === size ? "white" : primaryBlue[700],
                          fontWeight: 500,
                          border: `1px solid ${
                            selectedSize === size
                              ? primaryBlue[600]
                              : primaryBlue[200]
                          }`,
                          "&:hover": {
                            bgcolor:
                              selectedSize === size
                                ? primaryBlue[600]
                                : "rgba(0, 114, 206, 0.2)",
                            borderColor:
                              selectedSize === size
                                ? primaryBlue[700]
                                : primaryBlue[400],
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Colors - Critical choice for customers */}
              {item.colors && item.colors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: primaryBlue[800],
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Select Color{" "}
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                      *
                    </Typography>
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {item.colors.map((color, idx) => (
                      <Chip
                        key={idx}
                        label={color}
                        onClick={() => setSelectedColor(color)}
                        sx={{
                          bgcolor:
                            selectedColor === color
                              ? `${primaryBlue[500]}`
                              : "rgba(0, 114, 206, 0.1)",
                          color:
                            selectedColor === color
                              ? "white"
                              : primaryBlue[700],
                          fontWeight: 500,
                          border: `1px solid ${
                            selectedColor === color
                              ? primaryBlue[600]
                              : primaryBlue[200]
                          }`,
                          "&:hover": {
                            bgcolor:
                              selectedColor === color
                                ? primaryBlue[600]
                                : "rgba(0, 114, 206, 0.2)",
                            borderColor:
                              selectedColor === color
                                ? primaryBlue[700]
                                : primaryBlue[400],
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Required Options Message */}
              {((item.sizes && item.sizes.length > 0 && !selectedSize) ||
                (item.colors && item.colors.length > 0 && !selectedColor)) && (
                <Box
                  sx={{
                    mb: 3,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "rgba(255, 152, 0, 0.1)",
                    border: "1px solid rgba(255, 152, 0, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Info sx={{ color: "warning.main" }} />
                  <Typography variant="body2" sx={{ color: "warning.dark" }}>
                    Please select{" "}
                    {!selectedSize && item.sizes?.length > 0 ? "size" : ""}
                    {!selectedSize &&
                    item.sizes?.length > 0 &&
                    !selectedColor &&
                    item.colors?.length > 0
                      ? " and "
                      : ""}
                    {!selectedColor && item.colors?.length > 0 ? "color" : ""}{" "}
                    to proceed
                  </Typography>
                </Box>
              )}

              {/* Quantity selector - Critical for purchase */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: primaryBlue[800],
                    mb: 1,
                  }}
                >
                  Quantity
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      ...glassStyles,
                      bgcolor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "10px",
                      overflow: "hidden",
                      width: 140,
                    }}
                  >
                    <IconButton
                      onClick={handleDecreaseQuantity}
                      disabled={quantity <= item.minOrderQuantity}
                      sx={{ borderRadius: 0, p: 1 }}
                    >
                      <Remove />
                    </IconButton>

                    <TextField
                      value={quantity}
                      onChange={handleChangeQuantity}
                      inputProps={{
                        min: item.minOrderQuantity,
                        max: Math.min(
                          item.maxOrderQuantity,
                          item.stockQuantity
                        ),
                        style: {
                          textAlign: "center",
                          width: 40,
                          padding: "8px 0",
                        },
                      }}
                      variant="standard"
                      sx={{
                        "& .MuiInput-underline:before": {
                          borderBottom: "none",
                        },
                        "& .MuiInput-underline:after": { borderBottom: "none" },
                        "& .MuiInput-underline:hover:not(.Mui-disabled):before":
                          { borderBottom: "none" },
                      }}
                    />

                    <IconButton
                      onClick={handleIncreaseQuantity}
                      disabled={
                        quantity >=
                        Math.min(item.maxOrderQuantity, item.stockQuantity)
                      }
                      sx={{ borderRadius: 0, p: 1 }}
                    >
                      <Add />
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: primaryBlue[600],
                      fontWeight: 500,
                    }}
                  >
                    {item.stockQuantity > 0
                      ? `${item.stockQuantity} available`
                      : "Out of stock"}
                  </Typography>

                  {cartQuantity > 0 && (
                    <Chip
                      label={`${cartQuantity} in cart`}
                      size="small"
                      sx={{
                        ...glassStyles,
                        bgcolor: "rgba(76, 175, 80, 0.8)",
                        color: "white",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>

                {/* Buy Buttons */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ShoppingCart />}
                    onClick={handleAddToCart}
                    disabled={
                      item.stockQuantity === 0 ||
                      isMaxQuantityReached ||
                      (item.sizes && item.sizes.length > 0 && !selectedSize) ||
                      (item.colors && item.colors.length > 0 && !selectedColor)
                    }
                    sx={{
                      flex: 1,
                      py: 1.5,
                      borderColor: isMaxQuantityReached
                        ? "rgba(76, 175, 80, 0.5)"
                        : primaryBlue[300],
                      color: isMaxQuantityReached
                        ? "rgba(76, 175, 80, 0.8)"
                        : primaryBlue[600],
                      fontWeight: 600,
                      bgcolor: "rgba(255, 255, 255, 0.7)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "10px",
                      "&:hover": {
                        borderColor: primaryBlue[500],
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        opacity: isMaxQuantityReached ? 0.8 : 0.5,
                        borderColor: isMaxQuantityReached
                          ? "rgba(76, 175, 80, 0.5)"
                          : undefined,
                        color: isMaxQuantityReached
                          ? "rgba(76, 175, 80, 0.7)"
                          : undefined,
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {isMaxQuantityReached
                      ? `Max (${maxAllowedQuantity}) in Cart`
                      : "Add to Cart"}
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleBuyNow}
                    disabled={
                      item.stockQuantity === 0 ||
                      isMaxQuantityReached ||
                      (item.sizes && item.sizes.length > 0 && !selectedSize) ||
                      (item.colors && item.colors.length > 0 && !selectedColor)
                    }
                    sx={{
                      flex: 1,
                      py: 1.5,
                      background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "10px",
                      fontWeight: "bold",
                      boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                      "&:hover": {
                        background: `linear-gradient(45deg, #388e3c, #00BFFF)`,
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                      },
                      "&:disabled": {
                        opacity: 0.5,
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    {isMaxQuantityReached ? "Max Quantity Reached" : "Buy Now"}
                  </Button>
                </Box>
              </Box>

              {/* Simplified Tabs - Keep only essential information */}
              <Box sx={{ width: "100%" }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    "& .MuiTabs-indicator": {
                      backgroundColor: primaryBlue[500],
                      height: 3,
                      borderRadius: "3px 3px 0 0",
                    },
                    mb: 2,
                  }}
                >
                  <Tab
                    label="Description"
                    icon={<Description fontSize="small" />}
                    iconPosition="start"
                    sx={{
                      fontWeight: 600,
                      "&.Mui-selected": { color: primaryBlue[700] },
                    }}
                  />
                  <Tab
                    label="Specifications"
                    icon={<Info fontSize="small" />}
                    iconPosition="start"
                    sx={{
                      fontWeight: 600,
                      "&.Mui-selected": { color: primaryBlue[700] },
                    }}
                  />
                </Tabs>

                {/* Description Tab */}
                {activeTab === 0 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: primaryBlue[700], whiteSpace: "pre-line" }}
                    >
                      {item.description ||
                        "No description available for this product."}
                    </Typography>
                  </Box>
                )}

                {/* Specifications Tab - Only the most important specs */}
                {activeTab === 1 && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "rgba(255, 255, 255, 0.5)",
                      borderRadius: 2,
                    }}
                  >
                    <Grid container spacing={2}>
                      {item.material && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Material
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.material}
                          </Typography>
                        </Grid>
                      )}

                      {item.ageGroup && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Age Group
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.ageGroup}
                          </Typography>
                        </Grid>
                      )}

                      {item.weight && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Weight
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.weight}
                          </Typography>
                        </Grid>
                      )}

                      {item.warranty && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Warranty
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.warranty}
                          </Typography>
                        </Grid>
                      )}

                      {item.manufacturer && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Manufacturer
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {item.manufacturer}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ItemDetailsModal;
